import type { ProcedureType, Stored } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	ProcedureTypeEqualsFilterModifier,
	ProcedureTypeExcludesFilterModifier,
	ProcedureTypeIncludesFilterModifier,
	ProcedureTypeNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { ProcedureTypeFilterCell } from "./ProcedureTypeFilterCell"

type ProcedureTypeFieldValue = ProcedureType["uuid"]
type ProcedureTypeFilterValue = ProcedureType["uuid"][] | ProcedureType["uuid"]

export class ProcedureTypeFilterDef extends BaseFilterDef<ProcedureTypeFieldValue, ProcedureTypeFilterValue> {
	readonly id = "procedure_type"
	readonly defaultFilterValue: ProcedureTypeFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"
	readonly procedureTypes: Stored<ProcedureType>[]

	constructor(procedureTypes: Stored<ProcedureType>[]) {
		super()
		this.procedureTypes = procedureTypes
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: ProcedureTypeEqualsFilterModifier,
			isValueValid: (_value: ProcedureTypeFieldValue): _value is ProcedureType["uuid"] => true,
			isFilterValueValid: (filterValue: ProcedureTypeFilterValue): filterValue is ProcedureType["uuid"] =>
				!Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: ProcedureTypeNotEqualsFilterModifier,
			isValueValid: (_value: ProcedureTypeFieldValue): _value is ProcedureType["uuid"] => true,
			isFilterValueValid: (filterValue: ProcedureTypeFilterValue): filterValue is ProcedureType["uuid"] =>
				!Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: ProcedureTypeIncludesFilterModifier,
			isValueValid: (_value: ProcedureTypeFieldValue): _value is ProcedureType["uuid"] => true,
			isFilterValueValid: (filterValue: ProcedureTypeFilterValue): filterValue is ProcedureType["uuid"][] =>
				Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: ProcedureTypeExcludesFilterModifier,
			isValueValid: (_value: ProcedureTypeFieldValue): _value is ProcedureType["uuid"] => true,
			isFilterValueValid: (filterValue: ProcedureTypeFilterValue): filterValue is ProcedureType["uuid"][] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<ProcedureTypeFilterDef>): ReactNode {
		return <ProcedureTypeFilterCell key={props.filter.id} {...props} />
	}
}
